import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
// Importujeme moderní ikony
import { User, ShoppingCart, X, Trash2 } from 'lucide-react';

const Navbar = () => {
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  const { cartItems, cartCount, totalPrice, removeFromCart } = useCart();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  });

  return (
    <>
      <motion.header
        variants={{
          visible: { y: 0, opacity: 1 },
          hidden: { y: -100, opacity: 0 },
        }}
        animate={hidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        style={{
          position: 'fixed', top: 0, width: '100%', zIndex: 1000,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '15px 50px', background: 'rgba(43, 31, 24, 0.9)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="logo-container">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '15px', textDecoration: 'none' }}>
            <motion.img 
              src="images/logo.png" alt="Logo" 
              whileHover={{ rotate: -5, scale: 1.05 }}
              style={{ height: '45px' }}
            />
            <div className="logo">
              <strong style={{ fontSize: '1.5rem', color: '#fff', letterSpacing: '1px' }}>ŘEZBÁŘSTVÍ</strong>
            </div>
          </Link>
        </div>

        <nav style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
          <Link to="/o-mne" className="nav-link">O mně</Link>
          <Link to="/galerie" className="nav-link">Galerie</Link>
          <Link to="/eshop" className="nav-link">E-shop / Prodej</Link>
          <Link to="/kontakt" className="nav-link">Kontakty</Link>

          {/* Ikona Profilu - Moderní */}
          <Link to="/login" style={{ color: '#fff', marginLeft: '10px', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <motion.div whileHover={{ scale: 1.15, color: '#d1b892' }} style={{ display: 'flex' }}>
              <User size={22} strokeWidth={1.8} />
            </motion.div>
          </Link>

          {/* Ikona Košíku - Moderní */}
          <motion.div 
            whileHover={{ scale: 1.15, color: '#d1b892' }} 
            onClick={() => setIsCartOpen(true)}
            style={{ color: '#fff', cursor: 'pointer', position: 'relative', marginLeft: '15px', display: 'flex', alignItems: 'center' }}
          >
            <ShoppingCart size={22} strokeWidth={1.8} />
            {cartCount > 0 && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{
                  position: 'absolute', top: '-8px', right: '-10px',
                  background: '#d1b892', color: '#2b1f18',
                  borderRadius: '50%', padding: '2px 6px', fontSize: '0.65rem', fontWeight: 'bold',
                  minWidth: '18px', textAlign: 'center'
                }}
              >
                {cartCount}
              </motion.span>
            )}
          </motion.div>
        </nav>
      </motion.header>

      {/* --- VYSOUVACÍ KOŠÍK (OVERLAY) --- */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, backdropFilter: 'blur(4px)' }}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{ 
                position: 'fixed', right: 0, top: 0, bottom: 0, width: 'min(100%, 380px)', 
                background: '#f4e9dc', zIndex: 1200, padding: '40px 30px',
                boxShadow: '-10px 0 30px rgba(0,0,0,0.15)', color: '#2b1f18',
                display: 'flex', flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '800' }}>Váš košík</h2>
                <motion.button 
                  whileHover={{ rotate: 90, color: '#8b5a2b' }}
                  onClick={() => setIsCartOpen(false)} 
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
                >
                  <X size={28} />
                </motion.button>
              </div>

              <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '30px', paddingRight: '5px' }}>
                {cartItems.length === 0 ? (
                  <div style={{ textAlign: 'center', marginTop: '60px' }}>
                    <ShoppingCart size={48} style={{ opacity: 0.2, marginBottom: '15px' }} />
                    <p style={{ fontStyle: 'italic', color: '#666' }}>V košíku zatím nic není...</p>
                  </div>
                ) : (
                  cartItems.map((item, index) => (
                    <motion.div 
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={index} 
                      style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'center', background: '#fff', padding: '12px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.03)' }}
                    >
                      <img src={item.image} alt={item.name} style={{ width: '65px', height: '65px', objectFit: 'cover', borderRadius: '10px' }} />
                      <div style={{ flexGrow: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: '1.2' }}>{item.name}</div>
                        <div style={{ color: '#8b5a2b', fontWeight: '700', marginTop: '4px' }}>{item.price.toLocaleString()} Kč</div>
                      </div>
                      <motion.button 
                        whileHover={{ scale: 1.1, color: '#ff4444' }}
                        onClick={() => removeFromCart(index)} 
                        style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', padding: '5px' }}
                      >
                        <Trash2 size={18} />
                      </motion.button>
                    </motion.div>
                  ))
                )}
              </div>

              <div style={{ borderTop: '2px solid #d1b892', paddingTop: '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '1.3rem', marginBottom: '25px' }}>
                  <span>Celkem:</span>
                  <span style={{ color: '#8b5a2b' }}>{totalPrice.toLocaleString()} Kč</span>
                </div>
                
                {cartItems.length > 0 ? (
                  <Link 
                    to="/objednavka" 
                    onClick={() => setIsCartOpen(false)}
                    className="contact-button" 
                    style={{ textAlign: 'center', width: '100%', textDecoration: 'none', display: 'block', padding: '18px' }}
                  >
                    Dokončit objednávku
                  </Link>
                ) : (
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="contact-button" 
                    style={{ width: '100%', padding: '18px' }}
                  >
                    Zpět k produktům
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;